import React, { useState, useEffect } from 'react';
import { Save, Edit3, ExternalLink } from 'lucide-react';
import { candidatesApi } from '../../services/api';
import { useSofteamboardStore, normalizeStep } from '../../store/softeamboardStore';
import { Modal, ModalActions, Alert } from '../shared';
import StepDetailForm from './StepDetailForm';

const EditStepModal = ({ step, needId, candidateId, open, onClose, onStepUpdated }) => {
  const { updateStepInCache } = useSofteamboardStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    status: '',
    date: '',
    comment: '',
    propositionUrl: '',
    
    // CANDIDATE_SENT details
    candidateSent: {
      clientName: '',
      clientEmail: '',
      mailSentDate: '',
      followUpDate: ''
    },
    
    // CANDIDATE_INTERVIEW details
    candidateInterview: {
      invitationDate: '',
      interviewDate: '',
      interviewLocation: ''
    },
    
    // GO_NOGO details
    goNoGo: {
      decision: 'GO',
      projectedStartDate: '',
      tjm: '',
      commercialConcession: '',
      endDate: ''
    },
    
    // SALE details
    sale: {
      tjm: '',
      commercialConcession: '',
      contractType: 'CDI',
      startDate: '',
      endDate: ''
    }
  });

  // Only allow editing of entity steps
  const editableStepTypes = [
    { value: 'CANDIDATE_SENT', label: 'Candidate Sent', requiresDetail: true },
    { value: 'CANDIDATE_INTERVIEW', label: 'Interview Scheduled', requiresDetail: true },
    { value: 'GO_NOGO', label: 'Go/No-Go Decision', requiresDetail: true },
    { value: 'SALE', label: 'Sale Completed', requiresDetail: true }
  ];

  // Initialize form data when step changes
  useEffect(() => {
    if (step && open) {
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().slice(0, 16);
      };

      setFormData({
        status: step.status || step.type || 'CANDIDATE_IDENTIFIED',
        date: formatDateForInput(step.date),
        comment: step.comment || '',
        propositionUrl: step.propositionUrl || '',
        
        candidateSent: {
          clientName: step.detailCandidateSent?.clientName || '',
          clientEmail: step.detailCandidateSent?.clientEmail || '',
          mailSentDate: formatDateForInput(step.detailCandidateSent?.mailSentDate),
          followUpDate: formatDateForInput(step.detailCandidateSent?.followUpDate)
        },
        
        candidateInterview: {
          invitationDate: formatDateForInput(step.detailCandidateInterview?.invitationDate),
          interviewDate: formatDateForInput(step.detailCandidateInterview?.interviewDate),
          interviewLocation: step.detailCandidateInterview?.interviewLocation || ''
        },
        
        goNoGo: {
          decision: step.detailGoNoGo?.decision || 'GO',
          projectedStartDate: formatDateForInput(step.detailGoNoGo?.projectedStartDate),
          tjm: step.detailGoNoGo?.tjm?.toString() || '',
          commercialConcession: step.detailGoNoGo?.commercialConcession || '',
          endDate: formatDateForInput(step.detailGoNoGo?.endDate)
        },
        
        sale: {
          tjm: step.detailSale?.tjm?.toString() || '',
          commercialConcession: step.detailSale?.commercialConcession || '',
          contractType: step.detailSale?.contractType || 'CDI',
          startDate: formatDateForInput(step.detailSale?.startDate),
          endDate: formatDateForInput(step.detailSale?.endDate)
        }
      });
    }
  }, [step, open]);

  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.status) {
      setError('Step status is required');
      return false;
    }

    if (!formData.date) {
      setError('Date is required');
      return false;
    }

    // Validate step-specific details
    switch (formData.status) {
      case 'CANDIDATE_SENT':
        if (!formData.candidateSent.mailSentDate) {
          setError('Mail sent date is required for candidate sent step');
          return false;
        }
        if (!formData.candidateSent.followUpDate) {
          setError('Follow-up date is required for candidate sent step');
          return false;
        }
        break;

      case 'CANDIDATE_INTERVIEW':
        if (!formData.candidateInterview.invitationDate) {
          setError('Invitation date is required for interview step');
          return false;
        }
        if (!formData.candidateInterview.interviewDate) {
          setError('Interview date is required for interview step');
          return false;
        }
        break;

      case 'GO_NOGO':
        if (!formData.goNoGo.decision) {
          setError('Decision is required for Go/No-Go step');
          return false;
        }
        break;

      case 'SALE':
        if (!formData.sale.tjm || Number.isNaN(Number.parseFloat(formData.sale.tjm))) {
          setError('Valid TJM is required for sale step');
          return false;
        }
        if (!formData.sale.contractType) {
          setError('Contract type is required for sale step');
          return false;
        }
        if (!formData.sale.startDate) {
          setError('Start date is required for sale step');
          return false;
        }
        if (!formData.sale.endDate) {
          setError('End date is required for sale step');
          return false;
        }
        break;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare step data according to schema
      const stepData = {
        status: formData.status,
        date: new Date(formData.date).toISOString(),
        comment: formData.comment.trim() || null,
        propositionUrl: formData.propositionUrl.trim() || null
      };

      // Add step-specific details
      switch (formData.status) {
        case 'CANDIDATE_SENT':
          stepData.detailCandidateSent = {
            clientName: formData.candidateSent.clientName.trim() || null,
            clientEmail: formData.candidateSent.clientEmail.trim() || null,
            mailSentDate: new Date(formData.candidateSent.mailSentDate).toISOString(),
            followUpDate: new Date(formData.candidateSent.followUpDate).toISOString()
          };
          break;

        case 'CANDIDATE_INTERVIEW':
          stepData.detailCandidateInterview = {
            invitationDate: new Date(formData.candidateInterview.invitationDate).toISOString(),
            interviewDate: new Date(formData.candidateInterview.interviewDate).toISOString(),
            interviewLocation: formData.candidateInterview.interviewLocation
          };
          break;

        case 'GO_NOGO':
          stepData.detailGoNoGo = {
            decision: formData.goNoGo.decision,
            projectedStartDate: formData.goNoGo.projectedStartDate ? new Date(formData.goNoGo.projectedStartDate).toISOString() : null,
            tjm: formData.goNoGo.tjm ? Number.parseFloat(formData.goNoGo.tjm) : null,
            commercialConcession: formData.goNoGo.commercialConcession.trim() || null,
            endDate: formData.goNoGo.endDate ? new Date(formData.goNoGo.endDate).toISOString() : null
          };
          break;

        case 'SALE':
          stepData.detailSale = {
            tjm: Number.parseFloat(formData.sale.tjm),
            commercialConcession: formData.sale.commercialConcession.trim() || '',
            contractType: formData.sale.contractType,
            startDate: new Date(formData.sale.startDate).toISOString(),
            endDate: new Date(formData.sale.endDate).toISOString()
          };
          break;
      }

      // Use the API service which handles token refresh automatically
      const updatedStep = await candidatesApi.updateStep(step.id, stepData);
      
      // ✅ FIX: Update store cache immediately
      updateStepInCache(needId, candidateId, normalizeStep(updatedStep));
      
      onStepUpdated?.(updatedStep);
      onClose();
    } catch (err) {
      console.error('Error updating step:', err);
      setError(err.message || 'Failed to update step');
    } finally {
      setLoading(false);
    }
  };

  const renderStepDetails = () => {
    const selectedType = editableStepTypes.find(type => type.value === formData.status);
    if (!selectedType?.requiresDetail) return null;

    return <StepDetailForm stepType={formData.status} formData={formData} onInputChange={handleInputChange} />;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Modifier l'étape"
      subtitle="Modifiez les détails et informations de l'étape."
      icon={Edit3}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Informations de l'étape</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="edit-step-status" className="block text-sm font-medium text-gray-700 mb-1">
                    Statut / type *
                  </label>
                  <select id="edit-step-status"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {editableStepTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="edit-step-date" className="block text-sm font-medium text-gray-700 mb-1">
                    Date et heure *
                  </label>
                  <input id="edit-step-date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="edit-step-comment" className="block text-sm font-medium text-gray-700 mb-1">
                  Commentaire
                </label>
                <textarea id="edit-step-comment"
                  value={formData.comment}
                  onChange={(e) => handleInputChange('comment', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Add any additional notes or comments..."
                />
              </div>

            </div>

            {/* Step-specific details */}
            {renderStepDetails()}

            <details className="rounded-lg border border-gray-200 bg-white">
              <summary className="min-h-[44px] cursor-pointer px-4 py-3 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600">
                Salesforce (optionnel)
              </summary>
              <div className="border-t border-gray-200 p-4">
                <label htmlFor="edit-step-proposition-url" className="mb-1 block text-sm font-medium text-gray-700">URL de la proposition Salesforce</label>
                <div className="flex">
                  <input id="edit-step-proposition-url" type="url" value={formData.propositionUrl} onChange={(e) => handleInputChange('propositionUrl', e.target.value)} className="flex-1 rounded-l-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="https://domain.lightning.force.com/..." />
                  {formData.propositionUrl && <button type="button" onClick={() => window.open(formData.propositionUrl, '_blank')} className="min-h-[44px] min-w-[44px] rounded-r-lg bg-blue-600 px-3 text-white hover:bg-blue-700" title="Ouvrir la proposition dans Salesforce"><ExternalLink className="h-4 w-4" /></button>}
                </div>
              </div>
            </details>

            {/* Actions */}
            <ModalActions
              onCancel={onClose}
              loading={loading}
              submitLabel="Enregistrer"
              loadingLabel="Enregistrement..."
              submitIcon={Save}
            />
          </form>
    </Modal>
  );
};

export default EditStepModal;
